/**
 * Request deliverables — persistence against `request_deliverables` (#1450).
 *
 * Track B1 replaced the POC device-storage stub with the real table. Only the **bodies** of
 * `listDeliverables` and `saveDeliverables` changed, exactly as the stub's header promised:
 * callers (the Deliverables step, the submit dialog, `CreateRequest` and
 * `useDeliverablesEditSession`) are untouched. Nothing in this file touches device storage
 * any more, which is what the spec's grep gate checks.
 *
 * **Two endpoints, not five.** Production code touches storage in exactly two places, so the
 * API is a read of the whole list and a write of the whole list:
 *
 *   GET  /api/requests/:requestId/deliverables  -> { deliverables, total }
 *   PUT  /api/requests/:requestId/deliverables  <- { base: [...], deliverables: [...] }
 *
 * The `PUT` sends the complete desired list and the server diffs it and commits in one
 * transaction (FR15). That is why `addDeliverable`, `addDeliverables`, `updateDeliverable`,
 * `removeDeliverable` and `adoptDeliverables` are gone: with one write call there is nothing
 * for them to do, and keeping them would offer a way to write a single row outside the
 * transaction that makes the one-row-per-type invariant safe.
 *
 * One row per type per request is enforced by `UQ_request_deliverables_type`, not only here.
 *
 * Track C2 added the offline pair at the bottom of the file — `readDeliverables` and
 * `commitDeliverables`. `listDeliverables` and `saveDeliverables` stay exactly as they were,
 * network-only primitives, because the sync replay needs a call that always goes to the wire.
 */

import { api } from './api';
import { networkService } from './network';
import { offlineCache } from './offlineCache';

import type {
  RequestDeliverable,
  RequestDeliverableEntry,
  RequestDeliverableType,
} from '../types/requestDeliverables';

/**
 * The id deliverables are held under before the request exists. `App.tsx` passes this same
 * literal as `requestId` on `/create-request`, so the sidebar and the step agree without
 * either of them knowing about the other.
 *
 * It never reaches the network: unsaved deliverables live in `CreateRequest`'s `formData`
 * and are written against the real id the moment the server hands one back.
 */
export const NEW_REQUEST_DELIVERABLES_ID = 'new-request';

const endpoint = (requestId: string): string =>
  `/requests/${encodeURIComponent(requestId)}/deliverables`;

/**
 * The `code` the server sends with a refused write. Must stay in step with
 * `server/routes/request-deliverables.route.ts`, which is the only thing that sends it.
 */
const STALE_BASE_ERROR_CODE = 'DELIVERABLES_STALE_BASE';

/** The row shape the API returns. Wider than the client record, and deliberately so. */
interface DeliverableApiRecord {
  id: number;
  requestId: number | string;
  itemType: RequestDeliverableType;
  comment: string | null;
  createdAt: string;
}

interface DeliverablesEnvelope {
  deliverables?: DeliverableApiRecord[];
  total?: number;
}

/**
 * Narrows an API row to the client record. `requestId` is an `INT` on the server and a
 * string on the client (it is a route param everywhere it is used), so it is stringified
 * here rather than at every consumer.
 */
const toClientRecord = (row: DeliverableApiRecord): RequestDeliverable => ({
  id: row.id,
  requestId: String(row.requestId),
  itemType: row.itemType,
  comment: row.comment ?? null,
  createdAt: row.createdAt,
});

/**
 * Mints provisional ids for rows held in memory before they are saved.
 *
 * Server ids are authoritative once a list has been persisted — `IDENTITY(1,1)` assigns
 * them — so this exists only to keep pending rows distinct from one another inside
 * `applyDeliverableEntries`. Non-finite ids are skipped rather than folded in: one row
 * without an `id` would otherwise make `Math.max` return `NaN`, and every row added
 * afterwards would carry `id: NaN`, which is neither unique nor comparable.
 */
function nextId(deliverables: RequestDeliverable[]): number {
  return (
    deliverables.reduce(
      (max, deliverable) =>
        Number.isFinite(deliverable.id) ? Math.max(max, deliverable.id) : max,
      0,
    ) + 1
  );
}

/** Blank and whitespace-only comments are stored as `null`, not as an empty string. */
const normalizeComment = (comment: string): string | null =>
  comment.trim() ? comment.trim() : null;

/** Every deliverable on the request. One row per type (FR14). */
export async function listDeliverables(
  requestId: string,
): Promise<RequestDeliverable[]> {
  const response = await api.get<DeliverablesEnvelope>(endpoint(requestId));
  return (response?.deliverables ?? []).map(toClientRecord);
}

// ──── Pure list operations ────
//
// Cycle 4b made deliverables behave like every other request field: edits are held in
// memory and committed by Save. That needs the add / edit / remove semantics without a
// round trip, in two places that cannot share a controller — the Request Detail edit
// session and Create Request's `formData`. These are those semantics, as pure functions
// over a list, so both hold the *same* invariants (one row per type, trimmed comments) as
// the persisted path. None of them touches the network.

/**
 * Adds entries to an in-memory list. Types already present are left untouched. Ids come
 * from `nextId` over the growing list so pending rows are distinct; they are provisional
 * and are replaced by the server's own ids when the list is persisted.
 */
export function applyDeliverableEntries(
  deliverables: RequestDeliverable[],
  requestId: string,
  entries: RequestDeliverableEntry[],
): RequestDeliverable[] {
  const next = [...deliverables];
  for (const entry of entries) {
    if (next.some(deliverable => deliverable.itemType === entry.itemType)) {
      continue;
    }
    next.push({
      id: nextId(next),
      requestId,
      itemType: entry.itemType,
      comment: normalizeComment(entry.comment),
      createdAt: new Date().toISOString(),
    });
  }
  return next;
}

/** Edits a comment in an in-memory list. The type is the key and never changes (FR15). */
export function applyDeliverableComment(
  deliverables: RequestDeliverable[],
  itemType: RequestDeliverableType,
  comment: string,
): RequestDeliverable[] {
  return deliverables.map(deliverable =>
    deliverable.itemType === itemType
      ? { ...deliverable, comment: normalizeComment(comment) }
      : deliverable,
  );
}

/** Removes a type from an in-memory list (FR16 — removal is a delete, not an archive). */
export function withoutDeliverableType(
  deliverables: RequestDeliverable[],
  itemType: RequestDeliverableType,
): RequestDeliverable[] {
  return deliverables.filter(deliverable => deliverable.itemType !== itemType);
}

/**
 * True when two lists describe the same request. Compared on `(itemType, comment)` only:
 * ids are provisional on a pending list and `createdAt` moves on every add, so comparing
 * whole records would report "changed" for a list the user put back exactly as it was.
 */
export function deliverableListsMatch(
  a: RequestDeliverable[],
  b: RequestDeliverable[],
): boolean {
  if (a.length !== b.length) return false;
  const byType = new Map(
    b.map(deliverable => [deliverable.itemType, deliverable]),
  );
  return a.every(deliverable => {
    const other = byType.get(deliverable.itemType);
    return !!other && (other.comment ?? null) === (deliverable.comment ?? null);
  });
}

export interface DeliverableListDiff {
  added: RequestDeliverableEntry[];
  updated: RequestDeliverableEntry[];
  removedTypes: RequestDeliverableType[];
}

/**
 * What has to change to turn `base` into `next`. Keyed by type throughout.
 *
 * No longer on the write path — the server computes its own diff from the whole list — but
 * kept because it is how a caller answers "what did the user actually change?" without
 * asking the network, and it is the shape Track C's offline queue will replay.
 */
export function diffDeliverableLists(
  base: RequestDeliverable[],
  next: RequestDeliverable[],
): DeliverableListDiff {
  const baseByType = new Map(
    base.map(deliverable => [deliverable.itemType, deliverable]),
  );
  const nextTypes = new Set(next.map(deliverable => deliverable.itemType));

  const added: RequestDeliverableEntry[] = [];
  const updated: RequestDeliverableEntry[] = [];

  for (const deliverable of next) {
    const previous = baseByType.get(deliverable.itemType);
    const entry: RequestDeliverableEntry = {
      itemType: deliverable.itemType,
      comment: deliverable.comment ?? '',
    };
    if (!previous) {
      added.push(entry);
    } else if ((previous.comment ?? null) !== (deliverable.comment ?? null)) {
      updated.push(entry);
    }
  }

  const removedTypes = base
    .filter(deliverable => !nextTypes.has(deliverable.itemType))
    .map(deliverable => deliverable.itemType);

  return { added, updated, removedTypes };
}

/**
 * Commits a held list. The single write path for the deferred-persistence model: Request
 * Detail's Save and its submit both call this, and so does Create Request's first save.
 *
 * `next` is sent **whole**. The server diffs the two and commits the adds, edits and
 * removals in one transaction, which is what makes a save that adds one deliverable and
 * removes another atomic — the three-call client-side sequence this replaced could not be.
 *
 * `base` is sent too, and is no longer ignored. It is this caller's **proof of what it
 * saw**: the server permits only the deletions it can find in there and refuses the write
 * with 409 otherwise, so a client editing from a list it never really read can no longer
 * destroy rows. Callers that genuinely know nothing (a request created a moment ago) send
 * `[]` honestly, which forbids deletions and permits everything else.
 * (MR !489 review, finding 01, batch 1b.)
 *
 * An empty `next` is a valid write and clears the request: "at least one deliverable" is a
 * rule about submitting, enforced in the dialog, not about storage.
 */
export async function saveDeliverables(
  requestId: string,
  base: RequestDeliverable[],
  next: RequestDeliverable[],
): Promise<void> {
  await api.put<DeliverablesEnvelope>(endpoint(requestId), {
    // Types only. Membership is the whole of the proof; a comment cannot justify a deletion.
    base: base.map(deliverable => ({ itemType: deliverable.itemType })),
    deliverables: next.map(deliverable => ({
      itemType: deliverable.itemType,
      comment: deliverable.comment ?? null,
    })),
  });
}

/**
 * True when a failed write was the server refusing a deletion the caller could not prove,
 * rather than anything transient. The two need different answers: a stale base needs the
 * user to reload, a dropped connection needs a retry, and collapsing them sends the user to
 * the wrong remedy. (MR !489 review, AC1.8.3.)
 */
export function isStaleBaseError(error: unknown): boolean {
  // Matches the `code` the route deliberately sends, not the bare status. A 409 alone is
  // ambiguous — a proxy, a WAF or a future conflict on this path would produce one too — and
  // mistaking that for a stale base tells the user to reopen a request nothing has touched,
  // and marks a recoverable queued edit permanently failed.
  //
  // Duck-typed rather than `instanceof ApiError` so the check cannot throw on a non-object
  // and does not depend on both sides holding the same class instance, which matters where
  // the module is mocked. It does NOT rest on any import-cycle argument: an earlier version
  // of this comment claimed `api` could be mid-evaluation, which is false — `syncService.ts`
  // imports `ApiError` from `./api` directly and always has.
  const candidate = error as { status?: unknown; code?: unknown };
  return candidate?.status === 409 && candidate?.code === STALE_BASE_ERROR_CODE;
}

/**
 * Rejections a retry can never turn into a success.
 *
 * Anything the server answered with a 4xx is the request's own fault and will be answered
 * identically forever: an unknown deliverable type (400), a request deleted while this
 * device was offline (404), a base the server has already refused (409). A 5xx or a
 * transport failure is the opposite — the request may be fine and the moment was not — so
 * those stay queued and retry.
 *
 * Without this split every non-409 permanent rejection was re-sent on every sync, pushing a
 * fresh copy of the same error at the user each time. (MR !489 review, batch 1b.)
 */
export function isPermanentWriteRejection(error: unknown): boolean {
  const status = (error as { status?: unknown })?.status;
  return typeof status === 'number' && status >= 400 && status < 500;
}

// ──── Offline (Track C2) ────
//
// `api.ts` stays network-unaware on purpose — everything uses it, and what to do when the
// network is gone depends entirely on the caller. These two functions are that decision for
// deliverables, and they are the only place it is made. Storage is the request's own offline
// queues and its own cache entry (`offlineCache.queueDeliverables` /
// `updateCachedRequestDeliverables`); deliverables have no queue or storage key of their own.

/**
 * What `commitDeliverables` actually did, so the caller can skip a network re-read.
 *
 * `failed` exists because the queue write can fail — storage quota, an `o-*` entry that
 * synced out from under the save — and reporting that as `queued` cost the user their edit:
 * the caller promoted the list to `persisted` and cleared `pending`, discarding the only
 * remaining copy. A caller seeing `failed` must keep the pending list and say so.
 * (MR !489 review, finding 01 / the swallow cluster.)
 */
export type DeliverablesCommitOutcome =
  | 'sent'
  | 'queued'
  | 'skipped'
  | 'failed'
  /**
   * The server refused the write because it would have deleted rows this device never
   * proved it saw. Distinct from `failed` because the remedy is different: nothing is wrong
   * with the device or the connection, the list simply moved on and the user must reload.
   * Like `failed`, the caller must keep its pending edit. (MR !489 review, batch 1b.)
   */
  | 'conflict';

/**
 * Why a read could not establish a trustworthy base.
 *
 * `offline-no-cache` — no network and no local copy, so the device has never seen this list.
 * `load-failed` — the server read threw. A cached copy may still be attached, but it could be
 * arbitrarily stale, so it is not a base to edit from.
 */
export type DeliverablesUnavailableReason = 'offline-no-cache' | 'load-failed';

/**
 * A read either produced a trustworthy base or it did not, and the caller must say which.
 *
 * This is a union rather than an array plus flags on purpose. The previous shape returned
 * `[]` for both "this request has no deliverables" and "this device has never seen them",
 * and every wipe path in MR !489 finding 01 was that ambiguity cashing out: the save sends
 * the whole list and the server replaces what it holds, so editing from a falsely-empty base
 * deletes everything. Making the states distinct moves the problem from review to the
 * compiler — a caller cannot read `deliverables` without first narrowing on `status`.
 */
export type DeliverablesReadResult =
  | {
      status: 'loaded';
      deliverables: RequestDeliverable[];
      /**
       * True when the list came from the offline queue rather than the server — including
       * for an `o-*` request, whose list only ever lives on the queue.
       */
      fromQueue: boolean;
      /** True when there was no network. Lets the UI say so instead of showing an error. */
      offline: boolean;
      /**
       * What this device can prove the **server** holds, which is what the next save must
       * send as its base. Equal to `deliverables` in every case except a queued read: there,
       * `deliverables` is the user's own unsent `next`, and sending it as the base would
       * claim proof of the very removal the user made offline, so the server would refuse
       * exactly the write the queue exists to deliver.
       *
       * `[]` when nothing is known, which proves nothing and therefore permits no deletions.
       * (MR !489 review, batch 1b change 1.5.)
       */
      base: RequestDeliverable[];
    }
  | {
      status: 'unavailable';
      reason: DeliverablesUnavailableReason;
      /**
       * Whatever was in the cache, for display only. Never a base to save from: that is the
       * whole point of the `unavailable` status.
       */
      cached: RequestDeliverable[];
    };

/**
 * A request id that may reach a queue: anything but the unsaved-request sentinel. `o-*` ids
 * qualify — an offline-created request holds real deliverables, they just cannot be sent yet.
 */
const isQueueableRequestId = (requestId: string): boolean =>
  !!requestId && requestId !== NEW_REQUEST_DELIVERABLES_ID;

/**
 * A request id the server knows about — the only kind that may reach the wire.
 *
 * `o-*` is excluded deliberately. It is a client-minted id for a request that has not been
 * created yet, so `PUT /requests/o-1001/deliverables` 400s; and because the flush is the
 * first statement in RequestDetail's save, that 400 aborts the entire save, not just the
 * deliverables part of it.
 */
const isServerRequestId = (requestId: string): boolean =>
  isQueueableRequestId(requestId) && !requestId.startsWith('o-');

/**
 * Reads the list without assuming a network.
 *
 * Three sources in priority order. **Queued** first, always: a queued list is the user's own
 * unsynced intent and outranks whatever the server still holds, including in the window after
 * reconnecting but before sync runs. Then the **cached request**, which is what makes an
 * offline edit safe — the whole list is PUT on replay, so editing from an empty base would
 * delete every row the device never saw. Only then the **server**, and a successful read is
 * written back to the cache so the next offline reader has a real base.
 *
 * Never throws for "no local copy": an empty list with `offline: true` describes that
 * accurately, where "could not load" would send the user hunting for a fault.
 */
export async function readDeliverables(
  requestId: string,
): Promise<DeliverablesReadResult> {
  const online = networkService.isConnected();

  // The unsaved-request sentinel genuinely has no deliverables anywhere. That is a real
  // empty list, not an absent one.
  if (!isQueueableRequestId(requestId)) {
    return {
      status: 'loaded',
      deliverables: [],
      fromQueue: false,
      offline: !online,
      // Nothing exists on the server under this id, so there is nothing to have seen.
      base: [],
    };
  }

  const queued = await offlineCache.getQueuedDeliverables(requestId);
  if (queued !== null) {
    return {
      status: 'loaded',
      deliverables: queued,
      fromQueue: true,
      offline: !online,
      // The base the queued edit was built from, not the queued list itself. An entry
      // written before this branch has no stored base, and `[]` is the safe reading of
      // that: it proves nothing, so the replay may add and edit but not delete.
      base: (await offlineCache.getQueuedDeliverablesBase(requestId)) ?? [],
    };
  }

  // An `o-*` request exists only on this device, so the local store is the whole truth and
  // there is no server to fall through to. But a *missing* create-queue entry is not an
  // empty list: it means the create synced and this id is stale, which is precisely the
  // state that must not become a base to save from.
  if (!isServerRequestId(requestId)) {
    const offlineRequest = await offlineCache.getOfflineRequestById(requestId);
    if (!offlineRequest) {
      return { status: 'unavailable', reason: 'load-failed', cached: [] };
    }
    return {
      status: 'loaded',
      deliverables: offlineRequest.deliverables ?? [],
      fromQueue: true,
      offline: !online,
      // An `o-*` request does not exist on the server yet, so its stored list is entirely
      // additions and there is no server state to have seen.
      base: [],
    };
  }

  if (!online) {
    const cached = await offlineCache.getCachedRequestDeliverables(requestId);
    // `null` means the request is not cached at all — the device has never seen this list.
    // The previous `cached ?? []` collapsed that into "no deliverables" and handed the save
    // path an empty base. Keep the two apart.
    if (cached === null) {
      return { status: 'unavailable', reason: 'offline-no-cache', cached: [] };
    }
    return {
      status: 'loaded',
      deliverables: cached,
      fromQueue: false,
      offline: true,
      // The deliverables key on a cached request is only ever WRITTEN from server truth — a
      // successful read, save, or replay — so where it is present it is exactly what this
      // device saw.
      //
      // Known hole, pre-dating this change and not closed by it: a request cached by the
      // list screen carries no deliverables key at all, and `getCachedRequestDeliverables`
      // reports that as `[]` rather than `null`. Such a request reads as a trustworthy empty
      // list offline. The server-side check makes that safe — an empty base authorises no
      // deletion — but the list on screen is still an invention. Tracked in deferred work.
      base: cached,
    };
  }

  try {
    const deliverables = await listDeliverables(requestId);
    await offlineCache.updateCachedRequestDeliverables(requestId, deliverables);
    return {
      status: 'loaded',
      deliverables,
      fromQueue: false,
      offline: false,
      // Straight from the server a moment ago.
      base: deliverables,
    };
  } catch (error) {
    // Previously uncaught, so a transient 500 threw away a perfectly good cached list and
    // left the caller with an empty one it would then happily save.
    console.error(
      `Failed to read deliverables for request ${requestId}:`,
      error,
    );
    const cached = await offlineCache.getCachedRequestDeliverables(requestId);
    return {
      status: 'unavailable',
      reason: 'load-failed',
      cached: cached ?? [],
    };
  }
}

/**
 * Commits a list, queueing it instead of sending it when there is no network.
 *
 * `skipped` is an unsaved request (`NEW_REQUEST_DELIVERABLES_ID`): those deliverables live in
 * Create Request's form state and are written against the real id the server hands back, so
 * the sentinel must reach neither the queue nor the wire.
 *
 * An `o-*` request queues even when online, because there is nothing on the server to write
 * to yet; the sync flushes it against the real id once the create lands.
 *
 * `base` must be what the device can prove the **server** holds, which is what
 * `readDeliverables` now returns as `base`. Passing the list on screen instead is wrong
 * whenever that list came from the queue, and it is the difference between a queued removal
 * replaying and being refused.
 */
export async function commitDeliverables(
  requestId: string,
  base: RequestDeliverable[],
  next: RequestDeliverable[],
): Promise<DeliverablesCommitOutcome> {
  if (!isQueueableRequestId(requestId)) return 'skipped';

  if (!isServerRequestId(requestId) || !networkService.isConnected()) {
    // `queueDeliverables` never throws — an escape would abort RequestDetail's whole save,
    // see its docblock — but it does report, and a caller that treats a failed queue write
    // as `queued` throws away the user's only copy of the edit.
    //
    // The base rides along, because the replay is the thing that eventually has to prove it.
    // Deriving it at replay time from the queued list would prove the wrong thing entirely.
    const queued = await offlineCache.queueDeliverables(requestId, base, next);
    return queued ? 'queued' : 'failed';
  }

  try {
    await saveDeliverables(requestId, base, next);
  } catch (error) {
    // Only the stale-base refusal is answered here. Everything else keeps escaping to the
    // caller exactly as it did, because a transport failure is the caller's to report and
    // swallowing it would put this file back among the paths that claim success.
    if (!isStaleBaseError(error)) throw error;
    console.warn(
      `Deliverables for request ${requestId} were refused as stale:`,
      error,
    );
    return 'conflict';
  }
  // The cache is the base the next offline edit is built from, so it has to move with the
  // server. Failure here is swallowed inside the cache method: the write already landed.
  await offlineCache.updateCachedRequestDeliverables(requestId, next);
  // A queued list that outlived the write it was queued for is a loaded gun: the read path
  // prefers the queue, so the user sees their save "revert", and the next sync replays the
  // older list over the newer server state. (MR !489 review, finding 02.)
  await offlineCache.clearQueuedDeliverables(requestId);
  return 'sent';
}
